import Layout from 'components/layout';

import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';

import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import LucidContainer from 'components/lucid-container/lucid-container';
import NineteenInsights from 'components/nineteen/nineteen-insights/nineteen-insights';
import NineteenTextCta from 'components/nineteen/nineteen-text-cta/nineteen-text-cta';
import SunriseHeader from 'components/sunrise-header/sunrise-header';
import Tiles from 'components/tiles/tiles';
import {
  Drupal_FieldNodeNineteenSolutionsFieldColumnText,
  Drupal_FieldNodeNineteenSolutionsFieldHeaderCta,
  Drupal_NodeNineteenSolutions,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFeatureHighlightContents,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphNineteenInsights,
  Drupal_ParagraphNineteenTextCta
} from 'generated/graphql';
import { useMenuDefaultFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { TemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';

const Nineteen = ({ data, pageContext, location }: TemplateProps) => {
  const { langPrefix, pageType, nid, hideSsr } = pageContext;

  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeNineteenSolutions;
  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeNineteenSolutionsFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton
  );

  const columSections = (node.fieldColumnText as Drupal_FieldNodeNineteenSolutionsFieldColumnText[]).map(
    ({ entity }) => entity as Drupal_ParagraphFeatureHighlightContents
  );

  const tiles = node
    .fieldTiles!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphFullCard);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <Navbar menu={navbarMenu} scrollDistance={1} />
      <Box as="main" className="nineteen-solutions">
        {headerSection && (
          <SunriseHeader
            hasBackgroundImage
            smallBgImage
            backgroundImage={headerSection.fieldImage!}
            breadCrumbHub={langPrefix === 'en' ? 'Solutions' : ''}
            breadCrumbPage={node.fieldTitle!}
            buttons={headerCtas}
            header={headerSection.fieldHeader!}
            subheader={headerSection.fieldSubheader!}
          />
        )}
        <LucidContainer>
          <Heading as="h2" mb={3} mt={8} variant="subheadS">
            {node.fieldColumnsHeader}
          </Heading>
          <Box
            css={{
              display: 'flex',
              flexFlow: 'row wrap',
              justifyContent: 'space-between'
            }}
          >
            {columSections.map(
              ({ fieldColumnHeader, fieldColumnParagraph }) => (
                <Box
                  key={fieldColumnHeader}
                  flex={{ default: '0 1 100%', tabletSmall: '0 1 30%' }}
                  maxWidth="500px"
                >
                  <Heading as="h5" mt={6} variant="subheadXXS">
                    {fieldColumnHeader}
                  </Heading>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: getProcessedValue(fieldColumnParagraph)
                    }}
                  />
                </Box>
              )
            )}
          </Box>
        </LucidContainer>
        {node.fieldInsights && (
          <NineteenInsights
            entity={
              node.fieldInsights.entity as Drupal_ParagraphNineteenInsights
            }
          />
        )}
        {tiles && <Tiles bgColor="#323580" tiles={tiles} />}
        {node.fieldTextCta && (
          <NineteenTextCta
            entity={node.fieldTextCta.entity as Drupal_ParagraphNineteenTextCta}
          />
        )}
      </Box>
      <Footer
        entityTranslations={entityTranslations}
        menu={footerMenu}
      />
    </Layout>
  );
};

export default Nineteen;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeNineteenSolutions {
          ...entityFields
          fieldTitle
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
                fieldImage
              }
            }
          }
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonLink
                fieldButtonText
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldColumnsHeader
          fieldColumnText {
            entity {
              ... on Drupal_ParagraphFeatureHighlightContents {
                fieldColumnHeader
                fieldColumnParagraph {
                  processed
                }
              }
            }
          }
          fieldInsights {
            entity {
              ...nineteenInsights
            }
          }
          fieldTiles {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldTextCta {
            entity {
              ...nineteenTextCta
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
        }
      }
    }
  }
`;
